import { useState } from "react";
import { useAuthenticated } from "react-admin";
import { Box, Grid2 as Grid, Stack, Typography } from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Inventory2Outlined as TotalIcon,
  PendingActionsOutlined as InProcessIcon,
  SendOutlined as SubmittedIcon,
  CheckCircleOutlined as ReimbursedIcon,
  BlockOutlined as DeniedIcon,
  AddOutlined as AddIcon,
} from "@mui/icons-material";
import { Task, ResourceName, ClientCreate, VendorCreate } from "@/types";
import { TaskDetailsDialog } from "@/resources/tasks/components/task-dialogs/TaskDetailsDialog";
import { TaskCreateDialog } from "@/resources/tasks/components/task-dialogs/TaskCreateDialog";
import { CreateInDialog } from "@/components/common/CreateInDialog";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { MetricCard, MetricCardsGrid } from "@/components/common/MetricCards";
import { SectionCard } from "@/components/common/SectionCard";
import { CurrencyFormatterNotationOption, formatUSD } from "@/utils/formatters/currencyFormatter";

// Landing page components
import { LandingPageHeader } from "./components/LandingPageHeader";
import { TaskQueue } from "./components/TaskQueue";
import { RecentClaimsTable } from "./components/RecentClaimsTable";
import { ClaimsPipelineChart } from "./components/ClaimsPipelineChart";
import { ClaimsActivityChart } from "./components/ClaimsActivityChart";
import { UploadTypeDialog } from "./components/UploadTypeDialog";
import { YearSelector } from "./components/YearSelector";

// Hooks
import { useLandingPageData } from "./hooks/useLandingPageData";

const METRIC_ICONS = [
  <TotalIcon key="total" sx={{ fontSize: 18, color: "primary.main" }} />,
  <InProcessIcon key="process" sx={{ fontSize: 18, color: "primary.main" }} />,
  <SubmittedIcon key="submitted" sx={{ fontSize: 18, color: "primary.main" }} />,
  <ReimbursedIcon key="reimbursed" sx={{ fontSize: 18, color: "primary.main" }} />,
  <DeniedIcon key="denied" sx={{ fontSize: 18, color: "primary.main" }} />,
];

const METRIC_ACCENT_COLORS = ["primary.main", "primary.main", "primary.main", "primary.main", "primary.main"];

export const LandingPage = () => {
  useAuthenticated();

  const navigate = useNavigate();

  const [selectedYear, setSelectedYear] = useState<number | undefined>(undefined);

  const { canCreate: canCreateDocuments } = useResourcePermissions(ResourceName.DOCUMENTS);
  const { canCreate: canCreateTasks } = useResourcePermissions(ResourceName.TASKS);
  const { canCreate: canCreateClients } = useResourcePermissions(ResourceName.CLIENTS);
  const { canCreate: canCreatePolicies } = useResourcePermissions(ResourceName.POLICIES);
  const { canCreate: canCreateVendors } = useResourcePermissions(ResourceName.VENDORS);
  const { canList: canListClaims } = useResourcePermissions(ResourceName.STOPLOSS_CLAIMS);

  const {
    userName,
    stats,
    overdueTasks,
    todayTasks,
    upcomingTasks,
    laterTasks,
    actionableClaims,
    claimsByMonth,
    claimsByStatus,
    isLoading,
    chartsLoading,
  } = useLandingPageData(selectedYear);

  // Dialog state
  const [selectedTask, setSelectedTask] = useState<Task | undefined>();
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [createTaskOpen, setCreateTaskOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [createClientOpen, setCreateClientOpen] = useState(false);
  const [createVendorOpen, setCreateVendorOpen] = useState(false);

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
    setTaskDialogOpen(true);
  };

  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 5 }, py: { xs: 3, md: 4 } }}>
      {/* Header */}
      <LandingPageHeader
        userName={userName}
        onUploadClick={() => setUploadDialogOpen(true)}
        onCreateTaskClick={() => setCreateTaskOpen(true)}
        onCreateClientClick={() => setCreateClientOpen(true)}
        onCreatePolicyClick={() => navigate("/policies/create")}
        onCreateVendorClick={() => setCreateVendorOpen(true)}
        showUpload={canCreateDocuments}
        showCreateTask={canCreateTasks}
        showCreateClient={canCreateClients}
        showCreatePolicy={canCreatePolicies}
        showCreateVendor={canCreateVendors}
      />

      {/* Year filter + Metric Cards */}
      {canListClaims && (
        <Box sx={{ mt: 3, mb: 3 }}>
          <Box sx={{ mb: 2 }}>
            <YearSelector selectedYear={selectedYear} onChange={setSelectedYear} />
          </Box>
          <MetricCardsGrid columns={5}>
            {stats.map((stat, i) => (
              <MetricCard
                key={stat.label}
                icon={METRIC_ICONS[i]}
                title={stat.label}
                value={stat.count.toLocaleString()}
                accentColor={METRIC_ACCENT_COLORS[i]}
                hint={stat.hint}
                link={stat.link}
                isLoading={isLoading || chartsLoading}
                subMetrics={
                  stat.amount > 0
                    ? [
                        {
                          label: "Amount",
                          value: formatUSD(stat.amount, { notation: CurrencyFormatterNotationOption.COMPACT }),
                        },
                      ]
                    : undefined
                }
              />
            ))}
          </MetricCardsGrid>
        </Box>
      )}

      {/* Charts row: Activity + Pipeline — flex stretch so cards match heights */}
      {canListClaims && (
        <Grid container spacing={3} sx={{ alignItems: "stretch", mb: 3 }}>
          <Grid size={{ xs: 12, lg: 8 }} sx={{ display: "flex" }}>
            <Box
              component={RouterLink}
              to="/stoploss-claims"
              sx={{
                textDecoration: "none",
                color: "inherit",
                display: "flex",
                cursor: "pointer",
                flex: 1,
                "& > *": { flex: 1, mb: 0 },
              }}
            >
              <SectionCard title="Claims Activity" subtitle="Monthly claim volume by creation date" compact>
                <ClaimsActivityChart data={claimsByMonth} loading={chartsLoading} selectedYear={selectedYear} />
              </SectionCard>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }} sx={{ display: "flex" }}>
            <Box
              component={RouterLink}
              to="/stoploss-claims"
              sx={{
                textDecoration: "none",
                color: "inherit",
                display: "flex",
                cursor: "pointer",
                flex: 1,
                "& > *": { flex: 1, mb: 0 },
              }}
            >
              <SectionCard title="Claims Pipeline" subtitle="Current distribution by status" compact>
                <ClaimsPipelineChart data={claimsByStatus} loading={chartsLoading} />
              </SectionCard>
            </Box>
          </Grid>
        </Grid>
      )}

      {/* Lists row: Tasks + Recent Claims */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
              <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: "0.08em", color: "text.secondary" }}>
                My Tasks
              </Typography>
              {canCreateTasks && (
                <Typography
                  component="span"
                  variant="caption"
                  onClick={() => setCreateTaskOpen(true)}
                  sx={{
                    color: "primary.main",
                    fontWeight: 500,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.25,
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  <AddIcon sx={{ fontSize: 14 }} /> New Task
                </Typography>
              )}
            </Stack>
            <TaskQueue
              overdueTasks={overdueTasks}
              todayTasks={todayTasks}
              upcomingTasks={upcomingTasks}
              laterTasks={laterTasks}
              loading={isLoading}
              onTaskClick={handleTaskClick}
            />
          </Box>
        </Grid>

        {canListClaims && (
          <Grid size={{ xs: 12, lg: 6 }}>
            <RecentClaimsTable
              claims={actionableClaims}
              title="Claims Needing Action"
              emptyMessage="No claims need action"
            />
          </Grid>
        )}
      </Grid>

      {/* Dialogs */}
      {canCreateDocuments && <UploadTypeDialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} />}
      <TaskDetailsDialog open={taskDialogOpen} onClose={() => setTaskDialogOpen(false)} task={selectedTask} />
      {canCreateTasks && <TaskCreateDialog open={createTaskOpen} onClose={() => setCreateTaskOpen(false)} />}
      {canCreateClients && (
        <CreateInDialog<ClientCreate>
          open={createClientOpen}
          onClose={() => setCreateClientOpen(false)}
          resource={ResourceName.CLIENTS}
        />
      )}
      {canCreateVendors && (
        <CreateInDialog<VendorCreate>
          open={createVendorOpen}
          onClose={() => setCreateVendorOpen(false)}
          resource={ResourceName.VENDORS}
        />
      )}
    </Box>
  );
};
